import PropTypes from "prop-types";
import React from "react";
import { BsFillClockFill } from "react-icons/bs";
import {
    FaPiggyBank,
    FaMapMarkerAlt,
    FaSearch,
    FaThLarge,
    FaTable,
} from "react-icons/fa";
import { RiBuildingLine } from "react-icons/ri";

const filters = [];

function FilterToolbar({
    viewMode,
    onToggleView,
    onClearAllFilters,
    searchTerm,
    onSearchTermChange,
    selectedIndustry,
    onSelectIndustry,
    industryOptions,
    selectedLocation,
    onSelectLocation,
    locationOptions,
    selectedInvestmentRange,
    onSelectInvestmentRange,
    investmentRangeOptions,
    selectedTimelineRange,
    onSelectTimelineRange,
    timelineRangeOptions,
}) {
    const toolbarRef = React.useRef(null);
    const [showIndustryFilter, setShowIndustryFilter] = React.useState(false);
    const [showLocationFilter, setShowLocationFilter] = React.useState(false);
    const [showInvestmentFilter, setShowInvestmentFilter] =
        React.useState(false);
    const [showTimelineFilter, setShowTimelineFilter] = React.useState(false);
    const [showSearchFilter, setShowSearchFilter] = React.useState(false);

    const closeAllFilters = React.useCallback(() => {
        setShowIndustryFilter(false);
        setShowLocationFilter(false);
        setShowInvestmentFilter(false);
        setShowTimelineFilter(false);
        setShowSearchFilter(false);
    }, []);

    const selectedInvestmentLabel =
        investmentRangeOptions.find(
            (option) => option.value === selectedInvestmentRange,
        )?.label || "";

    const selectedTimelineLabel =
        timelineRangeOptions.find(
            (option) => option.value === selectedTimelineRange,
        )?.label || "";

    const hasActiveFilters = Boolean(
        selectedIndustry ||
            selectedLocation ||
            selectedInvestmentRange ||
            selectedTimelineRange,
    );

    const desktopFilterTrackClass =
        "relative -left-4 mx-auto w-full max-w-[31rem] xl:-left-8 xl:max-w-[36rem]";
    const desktopFilterButtonClass = "w-24 xl:w-28";

    const toggleIndustryFilter = () => {
        setShowIndustryFilter((current) => !current);
        setShowLocationFilter(false);
        setShowInvestmentFilter(false);
        setShowTimelineFilter(false);
        setShowSearchFilter(false);
    };

    const toggleLocationFilter = () => {
        setShowLocationFilter((current) => !current);
        setShowIndustryFilter(false);
        setShowInvestmentFilter(false);
        setShowTimelineFilter(false);
        setShowSearchFilter(false);
    };

    const toggleInvestmentFilter = () => {
        setShowInvestmentFilter((current) => !current);
        setShowIndustryFilter(false);
        setShowLocationFilter(false);
        setShowTimelineFilter(false);
        setShowSearchFilter(false);
    };

    const toggleTimelineFilter = () => {
        setShowTimelineFilter((current) => !current);
        setShowIndustryFilter(false);
        setShowLocationFilter(false);
        setShowInvestmentFilter(false);
        setShowSearchFilter(false);
    };

    const toggleSearchFilter = () => {
        setShowSearchFilter((current) => !current);
        setShowIndustryFilter(false);
        setShowLocationFilter(false);
        setShowInvestmentFilter(false);
        setShowTimelineFilter(false);
    };

    const clearAllAndClose = () => {
        onClearAllFilters();
        closeAllFilters();
    };

    const clearIndustryOnly = () => {
        onSelectIndustry("");
    };

    const clearLocationOnly = () => {
        onSelectLocation("");
    };

    const clearInvestmentOnly = () => {
        onSelectInvestmentRange("");
    };

    const clearTimelineOnly = () => {
        onSelectTimelineRange("");
    };

    const clearSearchOnly = () => {
        onSearchTermChange("");
    };

    React.useEffect(() => {
        const handlePointerDown = (event) => {
            if(
                toolbarRef.current &&
                !toolbarRef.current.contains(event.target)
            ) {
                closeAllFilters();
            }
        };

        const handleEscape = (event) => {
            if(event.key === "Escape") {
                closeAllFilters();
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [closeAllFilters]);

    return (
        <div
            ref={toolbarRef}
            style={{
                position: "sticky",
                top: "60px",
                zIndex: 30,
                width: "100%",
                background: "white",
                borderBottom:
                    viewMode === "table" ? "1px solid #D1E3EC" : "none",
            }}
            className="filter-toolbar"
        >
            {/* Mobile layout */}
            <div className="block lg:hidden">
                {/* Filter icons - mobile */}
                <div className="px-4 py-3">
                    <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 md:grid-cols-8">
                        <button
                            type="button"
                            onClick={toggleIndustryFilter}
                            className="flex flex-col items-center group focus:outline-none p-2"
                        >
                            <RiBuildingLine
                                size={20}
                                className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                            />
                            <span className="text-[#782375] hover:underline text-xs leading-tight text-center line-clamp-2">
                                Industry
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={toggleLocationFilter}
                            className="flex flex-col items-center group focus:outline-none p-2"
                        >
                            <FaMapMarkerAlt
                                size={20}
                                className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                            />
                            <span className="text-[#782375] hover:underline text-xs leading-tight text-center line-clamp-2">
                                Location
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={toggleInvestmentFilter}
                            className="flex flex-col items-center group focus:outline-none p-2"
                        >
                            <FaPiggyBank
                                size={20}
                                className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                            />
                            <span className="text-[#782375] hover:underline text-xs leading-tight text-center line-clamp-2">
                                Investment Amount
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={toggleTimelineFilter}
                            className="flex flex-col items-center group focus:outline-none p-2"
                        >
                            <BsFillClockFill
                                size={20}
                                className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                            />
                            <span className="text-[#782375] hover:underline text-xs leading-tight text-center line-clamp-2">
                                Timeline
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={toggleSearchFilter}
                            className="flex flex-col items-center group focus:outline-none p-2"
                        >
                            <FaSearch
                                size={20}
                                className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                            />
                            <span className="text-[#782375] hover:underline text-xs leading-tight text-center line-clamp-2">
                                Search
                            </span>
                        </button>
                        {filters.map(({ label, Icon }) => (
                            <button
                                key={label}
                                type="button"
                                className="flex flex-col items-center group focus:outline-none p-2"
                            >
                                <Icon
                                    size={20}
                                    className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                                />
                                <span className="text-[#782375] hover:underline text-xs leading-tight text-center">
                                    {label}
                                </span>
                            </button>
                        ))}
                    </div>

                    {showIndustryFilter && (
                        <div className="mt-3 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-3 shadow-sm">
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Select Industry
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <select
                                value={selectedIndustry}
                                onChange={(e) =>
                                    onSelectIndustry(e.target.value)
                                }
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            >
                                <option value="">All Industries</option>
                                {industryOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-3 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={clearAllAndClose}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    )}

                    {showLocationFilter && (
                        <div className="mt-3 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-3 shadow-sm">
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Select State
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <select
                                value={selectedLocation}
                                onChange={(e) => onSelectLocation(e.target.value)}
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            >
                                <option value="">All States</option>
                                {locationOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-3 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={clearAllAndClose}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    )}

                    {showInvestmentFilter && (
                        <div className="mt-3 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-3 shadow-sm">
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Select Minimum Investment Range
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <select
                                value={selectedInvestmentRange}
                                onChange={(e) =>
                                    onSelectInvestmentRange(e.target.value)
                                }
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            >
                                {investmentRangeOptions.map((option) => (
                                    <option
                                        key={option.value || "all-investments"}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-3 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={clearAllAndClose}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    )}

                    {showTimelineFilter && (
                        <div className="mt-3 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-3 shadow-sm">
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Select Time Left
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <select
                                value={selectedTimelineRange}
                                onChange={(e) =>
                                    onSelectTimelineRange(e.target.value)
                                }
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            >
                                {timelineRangeOptions.map((option) => (
                                    <option key={option.value || "all-timeline"} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-3 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={clearAllAndClose}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    )}

                    {showSearchFilter && (
                        <div className="mt-3 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-3 shadow-sm">
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Search Campaigns
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) =>
                                    onSearchTermChange(e.target.value)
                                }
                                placeholder="Search by campaign or company name"
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            />
                            <div className="mt-3 flex justify-end">
                                <button
                                    type="button"
                                    onClick={clearSearchOnly}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear search
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* View toggle - mobile */}
                <div className="flex justify-center items-center gap-2 pb-3 border-t border-gray-200 pt-3">
                    <button
                        onClick={() => onToggleView("grid")}
                        className={`
              w-10 h-10 rounded-md border flex items-center justify-center
              ${
                  viewMode === "grid"
                      ? "border-navy bg-navy text-paper"
                      : "border-navy bg-paper text-navy hover:bg-navy/10"
              }
            `}
                        title="Card view"
                    >
                        <FaThLarge size={16} />
                    </button>
                    <button
                        onClick={() => onToggleView("table")}
                        className={`
              w-10 h-10 rounded-md border flex items-center justify-center
              ${
                  viewMode === "table"
                      ? "border-navy bg-navy text-paper"
                      : "border-navy bg-paper text-navy hover:bg-navy/10"
              }
            `}
                        title="Table view"
                    >
                        <FaTable size={16} />
                    </button>
                </div>
            </div>

            {/* Desktop layout */}
            <div className="hidden lg:block">
                <div className="max-w-screen-2xl mx-auto px-6 py-3 xl:py-4">
                    <div className="relative">
                        {/* View toggle - desktop */}
                        <div className="absolute right-0 top-0 flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => onToggleView("grid")}
                                className={`
                w-11 h-11 rounded-md border flex items-center justify-center
                ${
                    viewMode === "grid"
                        ? "border-navy bg-navy text-paper"
                        : "border-navy bg-paper text-navy hover:bg-navy/10"
                }
              `}
                                title="Card view"
                            >
                                <FaThLarge size={18} />
                            </button>
                            <button
                                type="button"
                                onClick={() => onToggleView("table")}
                                className={`
                w-11 h-11 rounded-md border flex items-center justify-center
                ${
                    viewMode === "table"
                        ? "border-navy bg-navy text-paper"
                        : "border-navy bg-paper text-navy hover:bg-navy/10"
                }
              `}
                                title="Table view"
                            >
                                <FaTable size={18} />
                            </button>
                        </div>

                        {/* Filter icons - desktop */}
                        <div className={`${desktopFilterTrackClass} flex items-start justify-between gap-2 xl:gap-3`}>
                            <button
                                type="button"
                                onClick={toggleIndustryFilter}
                                className={`${desktopFilterButtonClass} flex shrink-0 flex-col items-center group focus:outline-none`}
                            >
                                <RiBuildingLine
                                    size={28}
                                    className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                                />
                                <span className="text-[#782375] hover:underline text-sm leading-tight text-center line-clamp-2">
                                    Industry
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={toggleLocationFilter}
                                className={`${desktopFilterButtonClass} flex shrink-0 flex-col items-center group focus:outline-none`}
                            >
                                <FaMapMarkerAlt
                                    size={28}
                                    className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                                />
                                <span className="text-[#782375] hover:underline text-sm leading-tight text-center line-clamp-2">
                                    Location
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={toggleInvestmentFilter}
                                className={`${desktopFilterButtonClass} flex shrink-0 flex-col items-center group focus:outline-none`}
                            >
                                <FaPiggyBank
                                    size={28}
                                    className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                                />
                                <span className="text-[#782375] hover:underline text-sm leading-tight text-center line-clamp-2">
                                    Investment Amount
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={toggleTimelineFilter}
                                className={`${desktopFilterButtonClass} flex shrink-0 flex-col items-center group focus:outline-none`}
                            >
                                <BsFillClockFill
                                    size={28}
                                    className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                                />
                                <span className="text-[#782375] hover:underline text-sm leading-tight text-center line-clamp-2">
                                    Timeline
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={toggleSearchFilter}
                                className={`${desktopFilterButtonClass} flex shrink-0 flex-col items-center group focus:outline-none`}
                            >
                                <FaSearch
                                    size={28}
                                    className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                                />
                                <span className="text-[#782375] hover:underline text-sm leading-tight text-center line-clamp-2">
                                    Search
                                </span>
                            </button>
                            {filters.map(({ label, Icon }) => (
                                <button
                                    key={label}
                                    type="button"
                                    className={`${desktopFilterButtonClass} flex shrink-0 flex-col items-center group focus:outline-none`}
                                >
                                    <Icon
                                        size={28}
                                        className="text-navy mb-1 group-hover:-translate-y-0.5 transition-transform"
                                    />
                                    <span className="text-[#782375] hover:underline text-sm leading-tight text-center">
                                        {label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {showIndustryFilter && (
                        <div className={`${desktopFilterTrackClass} mt-4 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-4 shadow-sm`}>
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Select Industry
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <select
                                value={selectedIndustry}
                                onChange={(e) => onSelectIndustry(e.target.value)}
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            >
                                <option value="">All Industries</option>
                                {industryOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-3 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={clearAllAndClose}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    )}

                    {showInvestmentFilter && (
                        <div className={`${desktopFilterTrackClass} mt-4 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-4 shadow-sm`}>
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Select Minimum Investment Range
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <select
                                value={selectedInvestmentRange}
                                onChange={(e) =>
                                    onSelectInvestmentRange(e.target.value)
                                }
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            >
                                {investmentRangeOptions.map((option) => (
                                    <option
                                        key={option.value || "all-investments"}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-3 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={clearAllAndClose}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    )}

                    {showLocationFilter && (
                        <div className={`${desktopFilterTrackClass} mt-4 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-4 shadow-sm`}>
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Select State
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <select
                                value={selectedLocation}
                                onChange={(e) => onSelectLocation(e.target.value)}
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            >
                                <option value="">All States</option>
                                {locationOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-3 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={clearAllAndClose}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    )}

                    {showTimelineFilter && (
                        <div className={`${desktopFilterTrackClass} mt-4 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-4 shadow-sm`}>
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Select Time Left
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <select
                                value={selectedTimelineRange}
                                onChange={(e) =>
                                    onSelectTimelineRange(e.target.value)
                                }
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            >
                                {timelineRangeOptions.map((option) => (
                                    <option key={option.value || "all-timeline"} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <div className="mt-3 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={clearAllAndClose}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    )}

                    {showSearchFilter && (
                        <div className={`${desktopFilterTrackClass} mt-4 rounded-xl border border-[#D1E3EC] bg-[#F5F7FA] p-4 shadow-sm`}>
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-navy">
                                    Search Campaigns
                                </label>
                                <button
                                    type="button"
                                    onClick={closeAllFilters}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Close
                                </button>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) =>
                                    onSearchTermChange(e.target.value)
                                }
                                placeholder="Search by campaign or company name"
                                className="w-full rounded-lg border border-navy bg-white p-2 text-sm text-navy outline-none"
                            />
                            <div className="mt-3 flex justify-end">
                                <button
                                    type="button"
                                    onClick={clearSearchOnly}
                                    className="text-xs font-medium text-[#782375] underline"
                                >
                                    Clear search
                                </button>
                            </div>
                        </div>
                    )}


                    {hasActiveFilters && (
                        <div className={`${desktopFilterTrackClass} mt-4 flex flex-wrap items-center justify-center gap-2 rounded-xl border border-[#D1E3EC] bg-white p-3 shadow-sm`}>
                            <span className="text-xs font-semibold uppercase tracking-wide text-navy">
                                Active Filters
                            </span>
                            {selectedIndustry && (
                                <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F0F4] px-3 py-1 text-sm font-medium text-navy">
                                    <span>Industry: {selectedIndustry}</span>
                                    <button
                                        type="button"
                                        onClick={clearIndustryOnly}
                                        className="text-[#782375] transition-colors hover:text-[#5a1b56]"
                                        aria-label="Remove industry filter"
                                        title="Remove industry filter"
                                    >
                                        X
                                    </button>
                                </span>
                            )}
                            {selectedLocation && (
                                <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F0F4] px-3 py-1 text-sm font-medium text-navy">
                                    <span>Location: {selectedLocation}</span>
                                    <button
                                        type="button"
                                        onClick={clearLocationOnly}
                                        className="text-[#782375] transition-colors hover:text-[#5a1b56]"
                                        aria-label="Remove location filter"
                                        title="Remove location filter"
                                    >
                                        X
                                    </button>
                                </span>
                            )}
                            {selectedInvestmentRange && (
                                <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F0F4] px-3 py-1 text-sm font-medium text-navy">
                                    <span>Min: {selectedInvestmentLabel}</span>
                                    <button
                                        type="button"
                                        onClick={clearInvestmentOnly}
                                        className="text-[#782375] transition-colors hover:text-[#5a1b56]"
                                        aria-label="Remove investment filter"
                                        title="Remove investment filter"
                                    >
                                        X
                                    </button>
                                </span>
                            )}
                            {selectedTimelineRange && (
                                <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F0F4] px-3 py-1 text-sm font-medium text-navy">
                                    <span>Time: {selectedTimelineLabel}</span>
                                    <button
                                        type="button"
                                        onClick={clearTimelineOnly}
                                        className="text-[#782375] transition-colors hover:text-[#5a1b56]"
                                        aria-label="Remove timeline filter"
                                        title="Remove timeline filter"
                                    >
                                        X
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

FilterToolbar.propTypes = {
    industryOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    investmentRangeOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        }),
    ).isRequired,
    timelineRangeOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        }),
    ).isRequired,
    locationOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        }),
    ).isRequired,
    onClearAllFilters: PropTypes.func.isRequired,
    onSearchTermChange: PropTypes.func.isRequired,
    onSelectIndustry: PropTypes.func.isRequired,
    onSelectInvestmentRange: PropTypes.func.isRequired,
    onSelectLocation: PropTypes.func.isRequired,
    onSelectTimelineRange: PropTypes.func.isRequired,
    searchTerm: PropTypes.string.isRequired,
    viewMode: PropTypes.string.isRequired,
    onToggleView: PropTypes.func.isRequired,
    selectedIndustry: PropTypes.string.isRequired,
    selectedInvestmentRange: PropTypes.string.isRequired,
    selectedLocation: PropTypes.string.isRequired,
    selectedTimelineRange: PropTypes.string.isRequired,
};

export default FilterToolbar;
